<template>
    <div v-if="ready">
        <form
            class="form-horizontal"
            enctype="multipart/form-data"
            @submit.prevent="store"
            @keydown="form.errors.clear($event.target.name)"
            novalidate
            autocomplete="off"
        >
            <form-input :form="form" required name="name"/>

            <backend-select
                :error="form.errors.get('depends_on')"
                name="depends_on"
                :route="route('trajectory-projects.trajectories.milestones.actions.index_all_milestones', {
                    trajectory_project: project.id,
                    trajectory: this.trajectory.id,
                    milestone: this.milestone.id
                })"
                label="depends_on"
                :value="field('depends_on')"
                v-model="form.depends_on"
                return="id"
                :prefix-data-with="[
                    {
                        id: 'start_of_trajectory',
                        name: uLang('app.start_of_trajectory')
                    },
                    {
                        id: 'end_of_trajectory',
                        name: uLang('app.end_of_trajectory')
                    }
                ]"
                required
            />

            <form-input :form="form" type="number"
                        name="lead_time_in_days" step="1" min="0" required/>

            <backend-select v-model="form.lead_time_of"
                :error="form.errors.get('lead_time_of')" name="lead_time_of" label="lead_time_of"
                :route="route('trajectory-projects.trajectories.milestones.actions.index_all_milestones', {
                    trajectory_project: project.id,
                    trajectory: this.trajectory.id,
                    milestone: this.milestone.id
                })"
                :value="field('lead_time_of')" return="id"
                :prefix-data-with="[
                    {
                        id: 'start_of_trajectory',
                        name: uLang('app.start_of_trajectory')
                    },
                    {
                        id: 'end_of_trajectory',
                        name: uLang('app.end_of_trajectory')
                    }
                ]"
                required
            />

            <backend-select v-model="form.assigned_to_role_id"
                            :error="form.errors.get('assigned_to_role_id')" name="assigned_to_role_id" label="assign_to_role"
                            :route="route('roles.index')" :value="field('assigned_to_role_id')" return="id"/>

            <backend-select v-model="form.action_document_category_id"
                            :error="form.errors.get('action_document_category_id')" name="action_document_category_id" label="action_document_category"
                            :route="route('action-document-categories.index')" :value="field('action_document_category_id')" return="id"/>

            <backend-select v-model="form.action_document_id"
                            :error="form.errors.get('action_document_id')" name="action_document_id" label="action_document"
                            :route="actionDocumentRoute" :value="field('action_document_id')" return="id"/>

            <yes-no-select  :error="form.errors.get('mail_alert')" v-model="form.mail_alert" name="mail_alert"/>

            <div class="form-group row">
                <label class="form-control-label col-md-3 col-xl-2 col-sm-12 col-form-label" for="do_not_create_sales_invoice"></label>
                <div class="col-md-9 col-xl-10 col-sm-12">
                    <label class="custom-control custom-checkbox mt-2">
                        <b-button
                            size="sm"
                            variant="link"
                            type="button"
                            class="text-small text-muted"
                            v-b-toggle.invoice_collapse
                            @click="invoiceOpened = !invoiceOpened"
                        >{{ uLang('app.invoice_settings') }} {{ invoiceOpened ? lang('app.close') : lang('app.open') }}
                            <i
                                class="fa"
                                :class="{ 'fa-plus': !invoiceOpened, 'fa-minus': invoiceOpened }"
                                :title="uLang('app.credit_the_invoice')"
                            ></i>
                        </b-button>
                    </label>
                </div>
            </div>

            <b-collapse id="invoice_collapse" class="mt-2">
                <price-with-type :error="form.errors.get('purchase_vat')" name="selling_price" v-model="selling_price"/>

                <!--@todo: Extract this to a form checkbox component-->
                <div class="form-group row">
                    <label class="form-control-label col-md-3 col-xl-2 col-sm-12 col-form-label" for="do_not_create_sales_invoice">{{ uLang('app.do_not_create_sales_invoice') }}</label>
                    <div class="col-md-9 col-xl-10 col-sm-12">
                        <label class="custom-control custom-checkbox mt-2">
                            <input type="checkbox" class="custom-control-input" id="do_not_create_sales_invoice" value="1" v-model="form.do_not_create_sales_invoice">
                            <span class="custom-control-indicator"></span>
                        </label>
                    </div>
                </div>

                <vat-select :error="form.errors.get('selling_vat')" v-model="form.selling_vat" name="selling_vat"/>

                <price-with-type :error="form.errors.get('purchase_vat')" name="purchase_price" v-model="purchase_price"/>

                <vat-select :error="form.errors.get('purchase_vat')" v-model="form.purchase_vat" name="purchase_vat"/>

                <form-textarea :form="form" name="invoice_description" rows="6"/>
            </b-collapse>


            <div class="form-group fr-absolute">
                <input :disabled="form.busy" type="submit" class="btn btn-brand"
                       :value="isUpdate() ? uLang('app.save') : uLang('app.add_type', { type: lang('app.action')})"/>
            </div>
        </form>
    </div>
</template>

<script>
    import Form from '~/Form/Form';
    import FormMixin from '~/mixins/form';
    import PriceWithType from '~/components/elements/price-with-type'
    import VatSelect from '~/components/elements/selects/vat'
    import YesNoSelect from '~/components/elements/selects/yes-no'
    import SuccessfullyStoredAlert from "~/Swals/SuccessfullyStoredAlert";
    import SuccessfullyUpdatedAlert from "~/Swals/SuccessfullyUpdatedAlert";

    export default {
        name: 'action-form',

        mixins: [FormMixin],

        props: {
            project: {
                type: Object,
                required: true,
            },
            trajectory: {
                type: Object,
                required: true,
            },
            milestone: {
                type: Object,
                required: true,
            },
            id: {
                required: false,
            },
        },

        components: {
            'price-with-type': PriceWithType,
            'vat-select': VatSelect,
            'yes-no-select': YesNoSelect,
        },

        data() {
            return {
                invoiceOpened: false,
                entity: 'action',
                form: new Form({
                    name: '',
                    depends_on: null,
                    lead_time_of: null,
                    lead_time_in_days: '',
                    purchase_price_amount: null,
                    purchase_price_type: null,
                    purchase_vat: 21,
                    selling_price_amount: null,
                    selling_price_type: null,
                    selling_vat: 21,
                    assigned_to_role_id: null,
                    action_document_category_id: null,
                    action_document_id: null,
                    do_not_create_sales_invoice: false,
                    invoice_description: null,
                    mail_alert: true,
                }),
                purchase_price: {
                    type: null,
                    amount: null,
                },
                selling_price: {
                    type: null,
                    amount: null,
                },
                swal: false,
            };
        },

        computed: {
            actionDocumentRoute() {
                if(this.form.action_document_category_id !== null) {
                    return route('action-documents.index', {
                        'no-concept': 'true',
                        'category': this.form.action_document_category_id
                    })
                }

                return route('action-documents.index', {'no-concept': 'true'})
            }
        },

        created() {
            this.$on('afterStore', this.afterStore);
            this.$on('afterUpdate', this.afterUpdate);

            this.$on('afterRetrieve', data => {
                this.purchase_price = {
                    amount: this.form.purchase_price_amount,
                    type: this.form.purchase_price_type,
                };

                this.selling_price = {
                    amount: this.form.selling_price_amount,
                    type: this.form.selling_price_type,
                };
            });
        },

        watch: {
            purchase_price() {
                this.form.purchase_price_amount = this.purchase_price.amount;
                this.form.purchase_price_type = this.purchase_price.type;
            },
            selling_price() {
                this.form.selling_price_amount = this.selling_price.amount;
                this.form.selling_price_type = this.selling_price.type;
            },
        },

        methods: {
            show() {
                this.$router.push({ name: 'admin.trajectory_projects.trajectories.milestones.actions.view', params: {
                    trajectory_project: this.projectId,
                    trajectory: this.trajectoryId,
                    milestone: this.milestoneId,
                    action: this.id,
                }});
            },

            afterStore(data) {
                new SuccessfullyStoredAlert({entity: this.lang('app.action')}).show()
                    .then((buttonPressed) => {
                        if (buttonPressed === 'create_another') {
                            // Form mixin clears fields already
                        } else if (buttonPressed === 'show') {
                            this.$router.push({name: 'admin.trajectory_projects.trajectories.milestones.actions.view', params: {
                                    projectId: this.project.id,
                                    trajectoryId: this.trajectory.id,
                                    milestoneId: this.milestone.id,
                                    id: data.id,
                                }});
                        }
                    });
            },

            afterUpdate(data) {
                new SuccessfullyUpdatedAlert({entity: this.lang('app.action')}).show()
                    .then(() => {
                        this.$router.push({name: 'admin.trajectory_projects.trajectories.milestones.actions.view', params: {
                                projectId: this.project.id,
                                trajectoryId: this.trajectory.id,
                                milestoneId: this.milestone.id,
                                id: this.id,
                            }});
                    });
            },
        },
    };
</script>

<style lang="css" scoped>

</style>
